/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Flex,
  Tabs as CTabs,
  Image,
  useToast,
  VStack,
  TabPanels,
  TabPanel,
  HStack,
  useDisclosure,
  Skeleton,
  Grid,
} from '@chakra-ui/react';
import { MainTemplate } from '../../templates/MainTemplate';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeProvider';
import { useMutation, useQueryClient } from 'react-query';
import { Text } from '../../components/atoms/Text';
import { Tabs } from './components/Tabs';
import { CardPromotions } from './components/CardPromotions';
import { DealMap } from './components/DealMap';
import { VirtualModal } from './components/VirtualModal';
import { ProductModal } from './components/ProductModal';
import { RateExperienceModal } from './components/RateExperienceModal';
import useIsMobile from '../../hooks/use-is-mobile';
import { HowToUse } from './components/HowToUse';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { useCheckPermission, usePromotionDetails } from '@/service/club/hooks';
import { useProfile } from '@/contexts/ProfileProvider';
import { toggleFavourite } from '@/service/club/api';

export const About = ({ description, site }: { description?: string; site?: string }) => {
  return (
    <VStack textAlign="left" alignItems="flex-start">
      <Text fontSize="16px" fontWeight={400} dangerouslySetInnerHTML={{ __html: description || '' }} />
      {site && site.length && (
        <HStack wordBreak="break-all">
          <i className="fas fa-at" />
          {site.includes('https://') ? (
            <a href={site} target="_blank" rel="noreferrer">
              {site}
            </a>
          ) : (
            <Text>{site}</Text>
          )}
        </HStack>
      )}
    </VStack>
  );
};

const tabs = ['Vantagens', 'Como Usar', 'Regulamento', 'Sobre'];

export function DealsDetailsPage() {
  const { mode, id } = useParams();
  const { colors, openModalLogin, id: agreementId, getValidSubscriptions } = useTheme();
  const { data, isFetching } = usePromotionDetails(`${mode ? mode + '_' : ''}${id}`);

  const [isFavorited, setFavorited] = useState(data?.favorite);

  const [selected, setSelected] = useState<any>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const { profile } = useProfile();

  const checkUserPermission = useCheckPermission(agreementId || 0);

  const modalExperienceRef = useRef<any>(null);
  const cinemaDisclosure = useDisclosure();
  const virtualDisclosure = useDisclosure();

  function toggleFavorite() {
    setFavorited(!isFavorited);

    const promotionId = data?.promotions[0].id;
    const storeId = data?.id.split('_')[1];
    if (promotionId && storeId) {
      toggleFavoriteMutation.mutate({ promotionId: String(promotionId), storeId });
    }
  }

  const toggleFavoriteMutation = useMutation(
    ({ promotionId, storeId }: { promotionId: string; storeId?: string }) => toggleFavourite(promotionId, storeId),
    {
      onSuccess() {
        queryClient.invalidateQueries('promotions');

        if (!isFavorited) {
          toast({
            title: 'Promoção favoritada com sucesso',
            status: 'success',
          });
        } else {
          toast({
            title: 'Promoção desfavoritada com sucesso',
            status: 'success',
          });
        }
      },
      onError() {
        toast({
          title: 'Você precisa estar logado para favoritar',
          status: 'error',
        });
      },
    }
  );

  const handleOpenPromotion = useCallback(
    (promotion: any) => {
      setSelected(promotion);
      if (!profile) {
        openModalLogin();
        return;
      }

      const subscriptions = getValidSubscriptions(profile);

      if (!checkUserPermission.isSuccess || subscriptions.length === 0) {
        openModalLogin();
        return;
      }

      if (data?.category === 'Cinema') {
        cinemaDisclosure.onOpen();
      } else {
        virtualDisclosure.onOpen();
      }
    },
    [checkUserPermission.isSuccess, cinemaDisclosure, data?.category, openModalLogin, profile, virtualDisclosure]
  );

  const getMainImage = useCallback(() => {
    if (!data) return undefined;

    if (data.coverPhoto) return data.coverPhoto;
    if (data.promotions) {
      const promotion = data.promotions.find((promotion) => promotion.image);
      if (promotion) return promotion.image;
    }
    return undefined;
  }, [data]);

  React.useEffect(() => {
    if (data?.favorite) {
      setFavorited(data.favorite);
    } else {
      setFavorited(false);
    }
  }, [data]);

  const hasHowToUse = () => {
    let found = null;

    if (data?.category === 'Cinema') return true;

    data?.promotions.forEach(({ howToUse }) => {
      if (howToUse) {
        found = howToUse;
      }
    });

    return found;
  };

  const renderTabs = () => {
    if (hasHowToUse() || data?.category === 'Cinema') {
      return tabs;
    } else {
      return tabs.filter((tab) => tab !== 'Como Usar');
    }
  };

  return (
    <MainTemplate>
      <VStack position={'relative'} width="100%" gap={0}>
        <VStack position={'relative'} width="100%" gap={0}>
          <Box position="relative" width="100%" display="flex" justifyContent="space-between">
            {isFetching ? (
              <Skeleton height={{ base: '250px', sm: '300px' }} width="100%" />
            ) : (
              getMainImage() && (
                <Image
                  src={getMainImage()}
                  width={'100%'}
                  height={{
                    base: '250px',
                    sm: '300px',
                  }}
                  objectFit="cover"
                />
              )
            )}

            <Box
              position={'absolute'}
              left={0}
              right={0}
              width="100%"
              height="100%"
              background="linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(255,255,255,0) 60%, rgba(0,0,0,0.40) 100%);"
              pointerEvents={'none'}
            />
            <Button variant={''} onClick={() => navigate(-1)} position={'absolute'} top={5}>
              <IoIosArrowRoundBack color="white" size={30} />
            </Button>
            <Box
              position={'absolute'}
              bottom={1}
              right={{
                base: '20px',
                md: '50px',
              }}
            >
              <Button variant={'link'} onClick={toggleFavorite}>
                {!isFavorited ? (
                  <AiOutlineHeart color="white" size={25} />
                ) : (
                  <AiFillHeart color={colors.primaryColor} size={25} />
                )}
              </Button>
            </Box>
          </Box>
          {data?.logo && (
            <Box
              left={{
                base: '20px',
                md: '50px',
              }}
              bottom={'-40px'}
              position={'absolute'}
            >
              {isFetching ? (
                <Skeleton borderRadius={12} width="85px" height="85px" />
              ) : (
                <Image
                  border={'2px solid white'}
                  borderRadius={12}
                  bg="white"
                  objectFit={'cover'}
                  src={data?.logo}
                  width="85px"
                  height="85px"
                />
              )}
            </Box>
          )}
        </VStack>
        <Flex
          direction={'column'}
          width={{
            base: '90%',
            lg: '100%',
          }}
          overflow={'hidden'}
          align={'flex-start'}
        >
          <HStack width="100%">
            {data?.logo && (
              <Box
                minWidth={{
                  base: '85px',
                  md: '135px',
                }}
              />
            )}
            {isFetching ? (
              <Skeleton marginTop="10px" height="30px" width="80%" />
            ) : (
              <Text
                letterSpacing={'-0.045em'}
                whiteSpace={'nowrap'}
                textOverflow={'ellipsis'}
                textAlign={'start'}
                fontWeight={700}
                width="80%"
                overflow={'hidden'}
                fontFamily={'Glory'}
                fontSize={28}
              >
                {data?.showName}
              </Text>
            )}
          </HStack>

          {isFetching ? (
            <Skeleton height="20px" marginTop="10px" width="100%" />
          ) : (
            <Text
              textOverflow={'ellipsis'}
              width={'100%'}
              color="#747474"
              textAlign={'start'}
              fontWeight={400}
              fontFamily={'Glory'}
              fontSize={16}
            >
              {data?.subtitle}
            </Text>
          )}
        </Flex>
        <VStack
          width={{
            base: '90%',
            lg: '100%',
          }}
          spacing={6}
          marginBottom="100px"
        >
          <CTabs isLazy borderColor="#E0E0E0" width="100%">
            <VStack width="100%" spacing={4} marginTop="15px">
              {isFetching ? (
                <Skeleton height="150px" width="100%" />
              ) : (
                <Text
                  fontSize={16}
                  fontFamily={'Glory'}
                  color="#747474"
                  width={'100%'}
                  fontWeight={400}
                  textAlign={'start'}
                >
                  {data?.description}
                </Text>
              )}
              <VStack
                width="100%"
                maxW={{
                  base: '100%',
                }}
                alignItems="flex-start"
                spacing={4}
              >
                <Tabs tabsList={renderTabs()} center={!isMobile} />

                <TabPanels>
                  <TabPanel padding={'0px'}>
                    <Grid
                      width="100%"
                      gap="15px"
                      alignItems="center"
                      justifyContent="center"
                      templateColumns={{
                        base: isMobile ? '1fr' : data?.promotions?.length === 1 ? '1fr' : '1fr 1fr',
                        sm: isMobile ? '1fr' : data?.promotions?.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                      }}
                    >
                      {isFetching
                        ? Array.from({ length: 4 }).map((_, idx) => (
                            <Skeleton key={idx} height="150px" width="100%" borderRadius="16px" margin="0 auto" />
                          ))
                        : data?.promotions.map((p) => (
                            <Box
                              key={p.id}
                              width={!isMobile && data?.promotions?.length === 1 ? '50%' : '100%'}
                              margin="0 auto"
                              display="flex"
                              justifyContent="center"
                            >
                              <CardPromotions {...p} onClick={() => handleOpenPromotion(p)} isMobile={isMobile} />
                            </Box>
                          ))}
                    </Grid>
                  </TabPanel>

                  {hasHowToUse() && (
                    <TabPanel padding={'0px'}>
                      <VStack spacing={6} width={'100%'}>
                        {isFetching ? (
                          <Skeleton height="20px" width="100%" />
                        ) : (
                          data?.promotions.map(({ id, howToUse }) => (
                            <VStack width={'100%'} key={id} textAlign="left" alignItems="flex-start">
                              {howToUse && (
                                <Text
                                  fontSize="16px"
                                  fontWeight={400}
                                  dangerouslySetInnerHTML={{ __html: howToUse.replaceAll('\n', '<br/>') }}
                                />
                              )}
                            </VStack>
                          ))
                        )}
                        <HowToUse name={data?.showName} />
                      </VStack>
                    </TabPanel>
                  )}
                  <TabPanel padding={'0px'}>
                    <VStack spacing={6}>
                      {isFetching ? (
                        <Skeleton height="20px" width="100%" />
                      ) : (
                        data?.promotions.map(({ id, rules }) => (
                          <VStack width={'100%'} key={id} textAlign="left" alignItems="flex-start">
                            <Text
                              fontSize="16px"
                              fontWeight={400}
                              dangerouslySetInnerHTML={{ __html: rules?.replaceAll('\n', '<br/>') }}
                            />
                          </VStack>
                        ))
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel padding={'0px'}>
                    {isFetching ? (
                      <Skeleton height="20px" width="100%" />
                    ) : (
                      <About description={data?.description} site={data?.site} />
                    )}
                  </TabPanel>
                </TabPanels>
              </VStack>

              {mode === 'local' && data?.latitude && data?.longitude && (
                <DealMap latitude={String(data?.latitude)} longitude={String(data?.longitude)} />
              )}
            </VStack>
          </CTabs>
        </VStack>
      </VStack>
      {!!profile && (
        <VirtualModal
          mode={data?.mode}
          modalExperienceRef={modalExperienceRef}
          discount={selected}
          link={selected?.link}
          code={selected?.coupon}
          discountId={selected?.id}
          onClose={virtualDisclosure.onClose}
          isOpen={virtualDisclosure.isOpen}
        />
      )}

      {!!profile && (
        <RateExperienceModal discount={data?.promotions[0]} ref={modalExperienceRef} companyName={data?.showName} />
      )}

      {!!profile && typeof data?.promotions[0] !== 'undefined' && (
        <ProductModal
          show_name={data?.showName}
          logo={data?.logo}
          rules={data?.promotions[0]?.rules}
          discount={data?.promotions[0]}
          onClose={cinemaDisclosure.onClose}
          isOpen={cinemaDisclosure.isOpen}
        />
      )}
    </MainTemplate>
  );
}
